import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiBarChart2,
  FiArrowRight,
  FiChevronDown,
  FiShoppingBag,
  FiClock,
  FiRefreshCw,
  FiDollarSign,
  FiMessageSquare,
  FiFileText,
  FiAlertCircle
} from 'react-icons/fi';

import { Link } from 'react-router-dom';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';
import WelcomeToArms from '../components/WelcomeToArms';
import ResourceUtilization from '../components/ResourceUtilization';


import { fetchProjects, fetchRuns } from '../services/api';



// Card component for reusable cards
const Card = ({ children, className }) => (
  <div className={`bg-white p-6 rounded-xl shadow-lg border border-gray-100 h-full hover:shadow-xl transition-shadow duration-300 ${className || ''}`}>
    {children}
  </div>
);



// VisualizationCard component for chart sections with standardized dimensions and overflow handling
const VisualizationCard = ({ title, children, action, subtitle, className, height = "320px" }) => (
  <div className={`w-full ${className || ''}`}>
    <Card className="h-full overflow-hidden">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-start mb-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 mb-1 truncate">{title}</h2>
            {subtitle && <p className="text-sm text-gray-600 truncate">{subtitle}</p>}
          </div>
          {action && <div className="ml-4 flex-shrink-0">{action}</div>}
      </div>
        <div className="flex-grow min-h-0 overflow-hidden">
          <div className="w-full h-full overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {children}
          </div>
        </div>
      </div>
    </Card>
  </div>
);



// TokenUsageChart component
const TokenUsageChart = ({ data }) => {
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value.toString();
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const point = payload[0].payload;
      // Use actual cost from data (same source as Project Distribution); fallback to estimate only if missing
      const cost = point.cost != null && point.cost >= 0
        ? point.cost
        : (value / 1000) * 0.002;
      const costStr = cost < 0.01 ? cost.toFixed(5) : cost.toFixed(2);
      
      return (
        <div className="bg-gradient-to-br from-white to-gray-50 p-3 shadow-lg rounded-lg border border-gray-100 backdrop-blur-sm">
          <div className="flex items-center mb-2">
            <div className="flex items-center">
              <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mr-2 animate-pulse"></div>
              <p className="font-semibold text-sm text-gray-900 font-inter">{label}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 font-inter font-medium">Tokens:</span>
              <span className="text-base font-bold text-gray-900 font-inter">
                {value.toLocaleString()}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 font-inter font-medium">Cost:</span>
              <span className="text-sm font-semibold text-emerald-600 font-inter">
                ${costStr}
              </span>
            </div>
            
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${Math.min((value / Math.max(...data.map(d => d.tokens || d.totalTokens || 0))) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!data || data.length === 0) {
      return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <FiBarChart2 className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-500 font-medium">No token usage data available</p>
          <p className="text-gray-400 text-sm mt-1">Start using tokens to see trends</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-white via-purple-50 to-pink-50 rounded-2xl p-4 shadow-lg border border-purple-100 overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
          margin={{ top: 20, right: 30, left: 60, bottom: 40 }}
            >
          <defs>
            <linearGradient id="tokenGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
              <stop offset="50%" stopColor="#EC4899" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#EF4444" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="25%" stopColor="#A855F7" />
              <stop offset="50%" stopColor="#EC4899" />
              <stop offset="75%" stopColor="#F472B6" />
              <stop offset="100%" stopColor="#EF4444" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="shadow">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#8B5CF6" floodOpacity="0.3"/>
            </filter>
          </defs>
          
          <CartesianGrid 
            strokeDasharray="12 12" 
            vertical={false} 
            stroke="#E5E7EB" 
            strokeOpacity={0.2}
          />
          
              <XAxis 
                dataKey="date" 
                axisLine={false} 
                tickLine={false}
            tick={{ 
              fontSize: 9, 
              fill: '#6B7280', 
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600
            }}
            dy={25}
            tickMargin={10}
            angle={0}
            textAnchor="middle"
            height={40}
            interval="preserveStartEnd"

          />
          
              <YAxis 
                axisLine={false} 
                tickLine={false} 
            tick={{ 
              fontSize: 10, 
              fill: '#6B7280', 
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600
            }}
            domain={[0, 'dataMax + dataMax * 0.15']}
                tickFormatter={formatYAxis}
                tickCount={6}
                dx={-5}
            tickMargin={5}

              />
          
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={30}
                wrapperStyle={{ 
                  fontSize: '11px', 
                  fontWeight: '700',
                  fontFamily: 'Inter, sans-serif',
                  color: '#374151'
                }}
                formatter={(value) => {
                  // Convert data key to readable label
                  if (value === 'tokens') return 'Token Usage';
                  if (value === 'totalTokens') return 'Total Tokens';
                  return value.charAt(0).toUpperCase() + value.slice(1);
                }}
              />
          
              {Object.keys(data[0])
                .filter(key => key !== 'date' && key !== 'cost')
                .map((key, index) => (
                  <Line 
                    key={index}
                    type="monotone" 
                    dataKey={key} 
                    name={key === 'tokens' ? 'Token Usage' : key === 'totalTokens' ? 'Total Tokens' : key.charAt(0).toUpperCase() + key.slice(1)}
                stroke="url(#lineGradient)"
                strokeWidth={3}
                fill="url(#tokenGradient)"
                fillOpacity={0.15}
                filter="url(#shadow)"
                dot={{
                  fill: '#8B5CF6',
                  stroke: '#ffffff',
                  strokeWidth: 2,
                  r: 5,
                  filter: "url(#glow)"
                }}
                activeDot={{
                  fill: '#EC4899',
                  stroke: '#ffffff',
                  strokeWidth: 3,
                  r: 7,
                  filter: "url(#glow)"
                }}
                className="hover:stroke-4 transition-all duration-300"
              />
            ))}
            </LineChart>
          </ResponsiveContainer>
          
          {/* Bottom Labels */}
          <div className="relative mt-3 pt-3 border-t border-gray-200">
            <div className="absolute bottom-2 left-0 text-sm text-gray-700 font-semibold">Token Usage</div>
            <div className="absolute bottom-2 right-0 text-sm text-gray-700 font-semibold">Date</div>
          </div>
        </div>
  );
};

// ProjectDistributionChart component - Enhanced Vertical Bar Chart
const ProjectDistributionChart = ({ data, projects }) => {
  // Prepare project data for vertical bar chart with better spacing
  const chartData = data.slice(0, 8).map(item => {
    const fullProject = projects.find(p => 
      (p.id === item.projectId) || 
      (p._id === item.projectId) || 
      (p.name === item.name)
    ) || {};
    
    // Use cost from item data first (already calculated from project totals), fallback to fullProject
    const cost = item.cost !== undefined && item.cost > 0 ? item.cost : (
      fullProject.total_cost || 
      (fullProject.costs && typeof fullProject.costs === 'object' && !Array.isArray(fullProject.costs)
        ? Object.values(fullProject.costs).reduce((a, b) => a + (parseFloat(b) || 0), 0)
        : 0)
    );
    
    return {
      name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
      fullName: item.name,
      runs: item.runs,
      tokens: item.totalTokens,
      cost: cost
    };
  });

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <FiBarChart2 className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-500 font-medium">No project distribution data available</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold text-sm text-gray-900 mb-2">{data.fullName}</p>
          <div className="space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="font-medium">Runs:</span>
              <span className="text-blue-600 font-semibold">{data.runs}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Tokens:</span>
              <span className="text-green-600 font-semibold">{data.tokens.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Cost:</span>
              <span className="text-purple-600 font-semibold">${((data.cost || 0) < 0.01 ? (data.cost || 0).toFixed(5) : (data.cost || 0).toFixed(2))}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full overflow-hidden">
        <ResponsiveContainer width="100%" height="100%">
        <BarChart 
          data={chartData} 
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          barCategoryGap={8}
          barSize={Math.max(15, Math.min(25, 200 / chartData.length))}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} stroke="#f0f0f0" />
            <XAxis 
              dataKey="name" 
              type="category" 
            label={{ 
              value: 'Projects', 
              position: 'insideBottom', 
              offset: -10,
              style: { fontSize: '11px', fontWeight: 'bold' }
            }}
            tick={{ fontSize: 9, fill: '#6B7280' }}
            axisLine={false}
            tickLine={false}
            angle={chartData.length > 6 ? -45 : 0}
            textAnchor={chartData.length > 6 ? 'end' : 'middle'}
            height={chartData.length > 6 ? 80 : 60}
            interval={0}
            />
            <YAxis 
              type="number"
            tick={{ fontSize: 10, fill: '#6B7280' }}
            axisLine={false}
            tickLine={false}
            tickMargin={10}
            />
            <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top" 
            height={30}
            wrapperStyle={{ fontSize: '12px', fontWeight: 'bold' }}
          />
            <Bar 
              dataKey="runs" 
            name="Number of Runs" 
              fill="#3B82F6" 
              radius={[4, 4, 0, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={`hsl(${210 + index * 30}, 70%, ${50 + index * 5}%)`}
            />
            ))}
          </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        {/* Bottom Labels */}
        <div className="flex justify-center mt-2 pt-2 border-t border-gray-100">
          <div className="text-sm text-gray-600 font-semibold">Projects</div>
        </div>
    </div>
  );
};

// ModelWiseDistributionChart component - Enhanced with Better Color Contrast
const ModelWiseDistributionChart = ({ data }) => {
  const getModelColor = (model) => {
    // Enhanced color palette with better contrast and accessibility
    const colors = {
      // OpenAI Models
      'gpt-4': '#1E40AF',           // Deep blue - high contrast
      'gpt-4o': '#3B82F6',          // Bright blue
      'gpt-4o-large': '#6366F1',    // Indigo
      'gpt-4o-mini': '#06B6D4',     // Cyan
      'gpt-4o-mini-2024-07-18': '#06B6D4', // Cyan (same as gpt-4o-mini)
      'gpt-3.5-turbo': '#0EA5E9',   // Sky blue
      'gpt-3.5': '#0EA5E9',         // Sky blue (alternative)
      
      // Anthropic Models
      'claude': '#7C3AED',          // Violet
      'claude-3': '#A855F7',        // Purple
      'claude-3.5': '#C084FC',      // Light purple
      'claude-3-opus': '#A855F7',   // Purple
      'claude-3-sonnet': '#C084FC', // Light purple
      'claude-3-haiku': '#E879F9',  // Pink
      
      // Meta Models
      'llama-3': '#F59E0B',         // Amber
      'llama-2': '#F97316',         // Orange
      'llama': '#F97316',           // Orange (alternative)
      
      // Mistral Models
      'mistral-7b': '#EF4444',      // Red
      'mistral-8x7b': '#DC2626',    // Dark red
      'mistral': '#EF4444',         // Red (alternative)
      
      // Google Models
      'gemini': '#10B981',          // Emerald
      'gemini-pro': '#059669',      // Dark emerald
      'gemini-1.5': '#10B981',      // Emerald
      'gemini-1.5-pro': '#059669',  // Dark emerald
      
      // Other Models
      'palm': '#84CC16',            // Lime
      'palm-2': '#84CC16',          // Lime
      'cohere': '#F472B6',          // Pink
      'cohere-command': '#F472B6',  // Pink
      'anthropic': '#7C3AED',       // Violet
      'openai': '#3B82F6',          // Blue
      'meta': '#F59E0B',            // Amber
      'google': '#10B981',          // Emerald
      
      // Default fallback
      'unknown': '#6B7280'          // Gray
    };
    
    // Try exact match first
    const exactMatch = colors[model.toLowerCase()];
    if (exactMatch) return exactMatch;
    
    // Try partial matches for model families
    const modelLower = model.toLowerCase();
    for (const [key, color] of Object.entries(colors)) {
      if (modelLower.includes(key) && key !== 'unknown') {
        return color;
      }
    }
    
    // Generate a consistent color based on model name hash
    const hash = modelLower.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    const hue = Math.abs(hash) % 360;
    const saturation = 70 + (Math.abs(hash) % 20); // 70-90%
    const lightness = 45 + (Math.abs(hash) % 15);  // 45-60%
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const getContrastColor = (backgroundColor) => {
    // Calculate contrast for better label visibility
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#FFFFFF';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const modelColor = getModelColor(data.model);
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <div className="flex items-center mb-2">
            <div 
              className="w-4 h-4 rounded-sm mr-2" 
              style={{ backgroundColor: modelColor }}
            />
            <p className="font-semibold text-sm text-gray-900">{data.model}</p>
          </div>
          <div className="space-y-1 text-xs text-gray-700">
            <div className="flex justify-between">
              <span className="font-medium">Runs:</span>
              <span className="text-blue-600 font-semibold">{data.runs}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Tokens:</span>
              <span className="text-green-600 font-semibold">{data.tokens.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Cost:</span>
              <span className="text-purple-600 font-semibold">${data.cost.toFixed(5)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Usage:</span>
              <span className="text-orange-600 font-semibold">{data.percentage}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, fill }) => {
    if (percent < 0.08) return null; // Don't show labels for slices less than 8%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Use contrast color for better readability
    const textColor = getContrastColor(fill);

    return (
      <text 
        x={x} 
        y={y} 
        fill={textColor} 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="11"
        fontWeight="600"
        fontFamily="Inter, sans-serif"
        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <FiBarChart2 className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-500 font-medium">No model distribution data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
            outerRadius={Math.min(80, Math.max(60, 120 / data.length))}
            innerRadius={Math.min(25, Math.max(15, 40 / data.length))}
                fill="#8884d8"
                dataKey="runs"
            paddingAngle={data.length > 8 ? 1 : 2}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getModelColor(entry.model)} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="top" 
            height={data.length > 6 ? 60 : 30}
            wrapperStyle={{ fontSize: data.length > 8 ? '10px' : '12px', fontWeight: 'bold' }}
            layout={data.length > 8 ? 'horizontal' : 'vertical'}
          />
            </PieChart>
          </ResponsiveContainer>
        </div>
  );
};

// AvailableModelsDisplay component - Shows all available LLM models with distinct colors


const Dashboard = () => {
  // Remove config and DB state
  const [timeFilter, setTimeFilter] = useState('All Time');
  const [projectsData, setProjectsData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Format number helper
  const formatNumber = useCallback((num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  }, []);
  
  // Derived state for dashboard metrics
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [avgLatency, setAvgLatency] = useState(0);
  const [tokenUsageData, setTokenUsageData] = useState([]);
  const [modelDistributionData, setModelDistributionData] = useState([]);
  const [modelWiseData, setModelWiseData] = useState([]);
  
  // Cost alert state

  



  // Helper to process and set dashboard metrics and chart data
  const processData = useCallback((projects, runs, backendTokenTotals = {}) => {
    setTotalProjects(projects?.length ?? 0);
    // When no projects exist (e.g. all deleted), show zeros so values recalculate after delete
    if (!projects || projects.length === 0) {
      setTotalTokens('0');
      setTokenUsageData([]);
      setModelDistributionData([]);
      setModelWiseData([]);
      setAvgLatency('0 ms');
      return;
    }
    // Token Usage Data
    const now = new Date();
    let days = 0;
    if (timeFilter === 'Last 7 Days') days = 7;
    else if (timeFilter === 'Last 30 Days') days = 30;
    else if (timeFilter === 'Last 90 Days') days = 90;
    // If 'All Time', show all data
    let tokenUsage = [];
    let totalTokensCount = 0;
    // Helper function to get tokens from a run (uses run-level fields first, then llm_calls)
    const getRunTokens = (run) => {
      // PRIORITY 1: Use common run-level fields (token_usage, tokens_used, total_tokens) - these are the source of truth
      if (run.token_usage && run.token_usage > 0) {
        return run.token_usage;
      }
      if (run.tokens_used && run.tokens_used > 0) {
        return run.tokens_used;
      }
      if (run.total_tokens && run.total_tokens > 0) {
        return run.total_tokens;
      }
      // PRIORITY 2: Fallback to calculating from llm_calls (for old data format compatibility)
      return run.llm_calls?.reduce((callSum, call) => callSum + (call.total_tokens || 0), 0) || 0;
    };

    // ✅ CRITICAL FIX: Check for backend totals FIRST (before client-side calculation)
    // This ensures we use accurate database totals when available
    let useBackendTotal = false;
    if (days === 7 && backendTokenTotals.total_tokens_7days !== null && backendTokenTotals.total_tokens_7days !== undefined) {
      totalTokensCount = backendTokenTotals.total_tokens_7days;
      useBackendTotal = true;
    } else if (days === 30 && backendTokenTotals.total_tokens_30days !== null && backendTokenTotals.total_tokens_30days !== undefined) {
      totalTokensCount = backendTokenTotals.total_tokens_30days;
      useBackendTotal = true;
    } else if (days === 90 && backendTokenTotals.total_tokens_90days !== null && backendTokenTotals.total_tokens_90days !== undefined) {
      totalTokensCount = backendTokenTotals.total_tokens_90days;
      useBackendTotal = true;
    } else if (days === 0 && backendTokenTotals.total_tokens_all_time !== null && backendTokenTotals.total_tokens_all_time !== undefined) {
      // For "All Time", use backend's all-time total if available
      totalTokensCount = backendTokenTotals.total_tokens_all_time;
      useBackendTotal = true;
    }
    
    // Token Usage: LLM cost from llm_calls; for agent/token runs with cost at run level, use run.cost when run has tokens (exclude embedding: 0 tokens)
    const getRunCostLLMOnly = (run) => {
      const fromLlm = (run.llm_calls || []).reduce((s, c) => s + (parseFloat(c.cost) || 0), 0);
      if (fromLlm > 0) return fromLlm;
      const runTokens = run.token_usage ?? run.tokens_used ?? run.total_tokens ?? 0;
      if (Number(runTokens) > 0) return parseFloat(run.cost) || parseFloat(run.total_cost) || 0;
      return 0;
    };
    // Only do client-side calculation if backend total is not available
    if (!useBackendTotal) {
      if (days > 0) {
        for (let i = days - 1; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(now.getDate() - i);
          const dateStr = date.toISOString().slice(0, 10);
          const dayRuns = runs.filter(run => run.start_time && run.start_time.slice(0, 10) === dateStr);
          const tokens = dayRuns.reduce((sum, run) => sum + getRunTokens(run), 0);
          const cost = dayRuns.reduce((sum, run) => sum + getRunCostLLMOnly(run), 0);
          tokenUsage.push({ date: dateStr, tokens, cost });
        }
      } else {
        // All Time: aggregate by date (tokens and cost - Token Usage chart: LLM cost only)
        const dateMap = {};
        runs.forEach(run => {
          if (run.start_time) {
            const dateStr = run.start_time.slice(0, 10);
            if (!dateMap[dateStr]) dateMap[dateStr] = { tokens: 0, cost: 0 };
            dateMap[dateStr].tokens += getRunTokens(run);
            dateMap[dateStr].cost += getRunCostLLMOnly(run);
            totalTokensCount += getRunTokens(run);
          }
        });
        tokenUsage = Object.entries(dateMap).map(([date, v]) => ({ date, tokens: v.tokens, cost: v.cost }));
      }
    } else {
      // If using backend total, still need to populate tokenUsage for the chart (Token Usage: LLM cost only)
      if (days > 0) {
        for (let i = days - 1; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(now.getDate() - i);
          const dateStr = date.toISOString().slice(0, 10);
          const dayRuns = runs.filter(run => run.start_time && run.start_time.slice(0, 10) === dateStr);
          const tokens = dayRuns.reduce((sum, run) => sum + getRunTokens(run), 0);
          const cost = dayRuns.reduce((sum, run) => sum + getRunCostLLMOnly(run), 0);
          tokenUsage.push({ date: dateStr, tokens, cost });
        }
      } else {
        // All Time: aggregate by date for chart (tokens and cost - Token Usage: LLM cost only)
        const dateMap = {};
        runs.forEach(run => {
          if (run.start_time) {
            const dateStr = run.start_time.slice(0, 10);
            if (!dateMap[dateStr]) dateMap[dateStr] = { tokens: 0, cost: 0 };
            dateMap[dateStr].tokens += getRunTokens(run);
            dateMap[dateStr].cost += getRunCostLLMOnly(run);
          }
        });
        tokenUsage = Object.entries(dateMap).map(([date, v]) => ({ date, tokens: v.tokens, cost: v.cost }));
      }
    }
    setTokenUsageData(tokenUsage);
    setTotalTokens(formatNumber(totalTokensCount));
    // Average Latency
    // Calculate average latency from ALL LLM calls
    const allLatencies = [];
    runs.forEach(run => {
      if (run.llm_calls?.length > 0) {
        run.llm_calls.forEach(call => {
          if (call.latency_ms) {
            allLatencies.push(call.latency_ms);
          }
        });
      }
    });
    const avg = allLatencies.length > 0 ? (allLatencies.reduce((a, b) => a + b, 0) / allLatencies.length) : 0;
    setAvgLatency(avg ? `${avg.toFixed(1)} ms` : '0 ms');
    // Project Distribution Data: use only project-level values (same as Project Details)
    const projectData = projects.map(project => {
      const id = project.id || project._id || project.project_id;
      const name = project.name || `Project ${id}`;
      const projectRuns = project.run_count ?? project.total_runs ?? 0;
      const projectTokens = project.token_usage ?? project.tokens_used ?? project.total_tokens ?? 0;
      let projectCost = 0;
      if (project.total_cost && project.total_cost > 0) {
        projectCost = project.total_cost;
      } else if (project.costs && typeof project.costs === 'object' && !Array.isArray(project.costs)) {
        projectCost = Object.values(project.costs).reduce((sum, cost) => sum + (parseFloat(cost) || 0), 0);
      } else if (project.cost && project.cost > 0) {
        projectCost = project.cost;
      }
      return {
        name,
        projectId: id,
        runs: projectRuns,
        totalTokens: projectTokens,
        cost: projectCost
      };
    })
    .filter(project => project.runs > 0 || project.totalTokens > 0) // Only show projects with activity
      .map(project => ({
        ...project,
        activityScore: project.runs + (project.totalTokens / 10000) // Weight tokens less than runs
      }))
      .sort((a, b) => b.activityScore - a.activityScore)
    .slice(0, Math.min(8, projects.length)) // Limit to top 8 projects
      .map(({ activityScore, ...rest }) => rest); // Remove temporary score
    
    setModelDistributionData(projectData);

    // Model-wise Distribution Data
    const modelUsage = {};
    let totalLLMCalls = 0;
    
    runs.forEach(run => {
      if (run.llm_calls && run.llm_calls.length > 0) {
        // Process ALL LLM calls in each run
        run.llm_calls.forEach(call => {
          const model = call.model || 'Unknown';
          if (!modelUsage[model]) {
            modelUsage[model] = {
              runs: 0,
              totalTokens: 0,
              totalCost: 0
            };
          }
          modelUsage[model].runs += 1;
          modelUsage[model].totalTokens += call.total_tokens || 0;
          modelUsage[model].totalCost += call.cost || 0;
          totalLLMCalls += 1;
        });
      }
    });
    
    const modelWiseDistribution = Object.entries(modelUsage).map(([model, stats]) => ({
      model,
      runs: stats.runs,
      tokens: stats.totalTokens,
      cost: stats.totalCost,
      percentage: totalLLMCalls > 0 ? (stats.runs / totalLLMCalls * 100).toFixed(1) : 0
    })).sort((a, b) => b.runs - a.runs).slice(0, 10); // Limit to top 10 models to prevent overflow
    
    // Model Distribution Data processed
    setModelWiseData(modelWiseDistribution);
    

  }, [timeFilter, formatNumber]);

  const loadRealData = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const projectsResponse = await fetchProjects();
        const runsResponse = await fetchRuns();
        // Handle both cases: when fetchProjects/fetchRuns return arrays directly or objects with properties
        const projects = Array.isArray(projectsResponse) ? projectsResponse : (projectsResponse?.projects || []);
        const runs = Array.isArray(runsResponse) ? runsResponse : (runsResponse?.runs || []);
        setProjectsData(projects);
        // ✅ CRITICAL FIX: Use backend's accurate token totals for all time periods (ClickHouse)
        // Handle both cases: when response is an object with projects array, or when it's just the object
        const backendTokenTotals = {
          total_tokens_7days: projectsResponse?.total_tokens_7days,
          total_tokens_30days: projectsResponse?.total_tokens_30days,
          total_tokens_90days: projectsResponse?.total_tokens_90days,
          total_tokens_all_time: projectsResponse?.total_tokens_all_time
        };
        // Debug: Log backend totals to verify they're being received
        if (process.env.NODE_ENV === 'development') {
          console.log('Backend Token Totals:', backendTokenTotals);
        }
        processData(projects, runs, backendTokenTotals);
      } catch (err) {
        setError('Failed to load dashboard data from database. Please check your connection.');
        if (process.env.NODE_ENV === 'development') {
          console.error('Dashboard data loading error:', err);
        }
      } finally {
        setLoading(false);
      }
  }, [processData]);

  useEffect(() => {
    // Always fetch real data from database (MongoDB, DynamoDB, or ClickHouse)
    loadRealData();
  }, [loadRealData]);

  // Listen for project data updates (e.g., when a run is deleted)
  useEffect(() => {
    const handleProjectDataUpdated = () => {
      loadRealData();
    };
    window.addEventListener('projectDataUpdated', handleProjectDataUpdated);
    return () => {
      window.removeEventListener('projectDataUpdated', handleProjectDataUpdated);
    };
  }, [loadRealData]);

  // Remove MongoConfigForm and config logic from render
  return (
    <div className="space-y-6">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
            <div className="text-gray-700">Loading dashboard data...</div>
          </div>
        </div>
      )}
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <FiAlertCircle className="mr-2" />
            {error}
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="text-red-700 hover:text-red-800 focus:outline-none"
          >
            <FiRefreshCw className="w-5 h-5" />
          </button>
        </div>
      )}
      

      {/* Dashboard Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 -m-6 p-6 mb-6 flex justify-between items-center border-b border-blue-100">
        <div className="flex items-center space-x-4">
        <div>
            <p className="text-gray-600 font-medium">Welcome,</p>
          <WelcomeToArms />
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <select 
              className="border border-gray-300 rounded-lg px-4 py-2 pr-8 appearance-none focus:outline-none bg-white font-medium shadow-sm"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
            >
              <option>All Time</option>
              <option>Last 7 Days</option>
              <option>Last 30 Days</option>
              <option>Last 90 Days</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <FiChevronDown className="w-4 h-4 text-gray-400" />
            </div>
          </div>
          

        </div>
      </div>
      {/* Data Analytics Overview Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Data Analytics Overview</h2>
            <p className="text-sm text-gray-600">Comprehensive insights into your AI model usage and performance</p>
      </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiShoppingBag className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Projects</p>
                <p className="text-xl font-bold text-gray-900">{totalProjects}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiBarChart2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Total Tokens {timeFilter === 'Last 7 Days' ? '(7 days)' : timeFilter === 'Last 30 Days' ? '(30 days)' : timeFilter === 'Last 90 Days' ? '(90 days)' : ''}
                </p>
                <p className="text-xl font-bold text-gray-900">{totalTokens}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FiClock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Avg. Latency</p>
                <p className="text-xl font-bold text-gray-900">{avgLatency}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Dashboard Grid - Enhanced Visualization Cards */}
      <div className="space-y-6">
        {/* Row 1: Token Usage & Project Distribution */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Token Usage Chart */}
          <div className="w-full h-[320px] min-h-[320px]">
          <VisualizationCard 
            title="Token Usage (Last 7 Days)" 
            subtitle={`Total: ${totalTokens} tokens`}
            className="h-full"

          >
            <TokenUsageChart data={tokenUsageData} />
          </VisualizationCard>
        </div>
        {/* Project Distribution */}
          <div className="w-full h-[320px] min-h-[320px]">
          <VisualizationCard 
            title="Project Distribution"
            subtitle="Number of Runs by Project"
            className="h-full"
          >
            <ProjectDistributionChart data={modelDistributionData} projects={projectsData} />
          </VisualizationCard>
        </div>
        </div>
        
        {/* Row 2: Model Distribution & System Resources */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Model-wise Distribution */}
          <div className="w-full h-[320px] min-h-[320px]">
          <VisualizationCard 
            title="Model Distribution"
            subtitle="Usage by AI Model"
            className="h-full"
          >
            <ModelWiseDistributionChart data={modelWiseData} />
          </VisualizationCard>
        </div>
        {/* System Resources */}
          <div className="w-full h-[320px] min-h-[320px]">
          <ResourceUtilization />
        </div>
      </div>


      </div>

      {/* Quick Analytics Links - Enhanced */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/cost-analytics" className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 group">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 group-hover:scale-105 transition-transform duration-200">
              <FiDollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Cost Analytics</h3>
              <p className="text-sm text-gray-600">Monitor API usage costs</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors">
            View Details <FiArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link to="/query-analytics" className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 group">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 group-hover:scale-105 transition-transform duration-200">
              <FiMessageSquare className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Query Analytics</h3>
              <p className="text-sm text-gray-600">Analyze prompts & responses</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors">
            View Details <FiArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        <Link to="/log-analytics" className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 group">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 group-hover:scale-105 transition-transform duration-200">
              <FiFileText className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Log Analytics</h3>
              <p className="text-sm text-gray-600">Monitor system logs</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium group-hover:text-blue-700 transition-colors">
            View Details <FiArrowRight className="ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>
      

    </div>
  );
};

export default Dashboard;